import React from 'react';
import { toast } from 'react-toastify';
import {
    deleteGroup,
    getGroupById,
    getGroupUsers,
    updateGroup,
} from '../../../../../helpers/group-helper';

const statusClassMap = {
    ACTIVE: 'bg-soft-success text-success',
    INACTIVE: 'bg-soft-danger text-danger',
};

const formatDate = (isoDate) => {
    if (!isoDate) return '-';

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

export const useGroupDetailScreen = ({ token, id, navigate, location }) => {
    const [group, setGroup] = React.useState(null);
    const [users, setUsers] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSavingGeneral, setIsSavingGeneral] = React.useState(false);
    const [isSavingUsers, setIsSavingUsers] = React.useState(false);
    const [isDisabling, setIsDisabling] = React.useState(false);
    const [error, setError] = React.useState('');
    const [activeTab, setActiveTab] = React.useState('general');

    const [generalForm, setGeneralForm] = React.useState({
        name: '',
        status: 'ACTIVE',
    });
    const [selectedUserIds, setSelectedUserIds] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState('');

    const tabs = React.useMemo(() => ([
        { key: 'general', label: 'General' },
        { key: 'users', label: 'Usuarios' },
    ]), []);

    React.useEffect(() => {
        const loadData = async () => {
            if (!token || !id) {
                setError('No se pudo obtener el grupo.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const [groupResp, usersResp] = await Promise.all([
                getGroupById(token, id),
                getGroupUsers(token),
            ]);

            if (!groupResp?.success) {
                setError(groupResp?.message || 'No se pudo obtener el grupo.');
                setGroup(null);
                setIsLoading(false);
                return;
            }

            if (!usersResp?.success) {
                setError(usersResp?.message || 'No se pudo obtener la lista de usuarios del tenant.');
                setGroup(groupResp.data || null);
                setUsers([]);
                setIsLoading(false);
                return;
            }

            const nextGroup = groupResp.data || null;
            const nextUsers = Array.isArray(usersResp.data) ? usersResp.data : [];

            setGroup(nextGroup);
            setUsers(nextUsers);
            setGeneralForm({
                name: nextGroup?.name || '',
                status: nextGroup?.status || 'ACTIVE',
            });
            setSelectedUserIds(Array.isArray(nextGroup?.users)
                ? nextGroup.users.map((user) => (typeof user === 'object' ? user?._id : user)).filter(Boolean)
                : []);

            const fetchedGroupName = nextGroup?.name;
            const currentGroupName = location.state?.groupName;

            if (fetchedGroupName && fetchedGroupName !== currentGroupName) {
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...(location.state || {}),
                        groupName: fetchedGroupName,
                    },
                });
            }

            setIsLoading(false);
        };

        loadData();
    }, [token, id, navigate, location.pathname, location.state]);

    const usersById = React.useMemo(() => {
        return users.reduce((accumulator, user) => {
            if (!user?._id) return accumulator;

            return {
                ...accumulator,
                [user._id]: user,
            };
        }, {});
    }, [users]);

    const groupUsersById = React.useMemo(() => {
        const existingUsers = Array.isArray(group?.users) ? group.users : [];
        return existingUsers.reduce((accumulator, user) => {
            if (!user?._id) return accumulator;

            return {
                ...accumulator,
                [user._id]: user,
            };
        }, {});
    }, [group?.users]);

    const selectedUsers = React.useMemo(() => {
        return selectedUserIds.map((userId) => {
            const existingUser = usersById[userId] || groupUsersById[userId];
            if (existingUser) {
                return existingUser;
            }

            return {
                _id: userId,
                name: 'Usuario no disponible',
                email: '-',
                role: '-',
            };
        });
    }, [selectedUserIds, usersById, groupUsersById]);

    const filteredUsers = React.useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const selectedSet = new Set(selectedUserIds);

        if (!normalizedSearch) {
            return [];
        }

        return users
            .filter((user) => {
                const userId = user?._id;
                if (!userId || selectedSet.has(userId)) {
                    return false;
                }

                const normalizedName = String(user?.name || '').toLowerCase();
                const normalizedEmail = String(user?.email || '').toLowerCase();
                const normalizedRole = String(user?.role || '').toLowerCase();

                return normalizedName.includes(normalizedSearch)
                    || normalizedEmail.includes(normalizedSearch)
                    || normalizedRole.includes(normalizedSearch);
            })
            .slice(0, 20);
    }, [users, selectedUserIds, searchTerm]);

    const handleGeneralInputChange = React.useCallback((event) => {
        const { name, value } = event.target;
        setGeneralForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleSaveGeneral = React.useCallback(async () => {
        if (!token || !id || isSavingGeneral) {
            return;
        }

        setIsSavingGeneral(true);
        setError('');
        const savingToastId = toast.loading('Guardando cambios del grupo...');

        const resp = await updateGroup(token, id, {
            name: generalForm.name,
            status: generalForm.status,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar el grupo.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSavingGeneral(false);
            return;
        }

        setGroup(resp.data || null);
        setGeneralForm({
            name: resp?.data?.name || '',
            status: resp?.data?.status || 'ACTIVE',
        });
        toast.update(savingToastId, {
            render: resp?.message || 'Grupo actualizado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSavingGeneral(false);
    }, [token, id, isSavingGeneral, generalForm]);

    const handleAddUser = React.useCallback((userId) => {
        setSelectedUserIds((previous) => {
            if (previous.includes(userId)) {
                return previous;
            }

            return [...previous, userId];
        });

        setSearchTerm('');
    }, []);

    const handleRemoveUser = React.useCallback((userId) => {
        setSelectedUserIds((previous) => previous.filter((item) => item !== userId));
    }, []);

    const handleSaveUsers = React.useCallback(async () => {
        if (!token || !id || isSavingUsers) {
            return;
        }

        setIsSavingUsers(true);
        setError('');
        const savingToastId = toast.loading('Guardando usuarios del grupo...');

        const resp = await updateGroup(token, id, {
            users: selectedUserIds,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar los usuarios del grupo.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSavingUsers(false);
            return;
        }

        const nextGroup = resp.data || null;
        setGroup(nextGroup);
        setSelectedUserIds(Array.isArray(nextGroup?.users)
            ? nextGroup.users.map((user) => (typeof user === 'object' ? user?._id : user)).filter(Boolean)
            : []);
        toast.update(savingToastId, {
            render: resp?.message || 'Usuarios del grupo actualizados correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSavingUsers(false);
    }, [token, id, isSavingUsers, selectedUserIds]);

    const handleDisableGroup = React.useCallback(async () => {
        if (!token || !id || isDisabling) {
            return;
        }

        setIsDisabling(true);
        setError('');
        const savingToastId = toast.loading('Inhabilitando grupo...');

        const resp = await deleteGroup(token, id);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo inhabilitar el grupo.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsDisabling(false);
            return;
        }

        toast.update(savingToastId, {
            render: resp?.message || 'Grupo inhabilitado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });

        navigate('/group/list');
    }, [token, id, isDisabling, navigate]);

    return {
        group,
        isLoading,
        isSavingGeneral,
        isSavingUsers,
        isDisabling,
        error,
        activeTab,
        tabs,
        generalForm,
        searchTerm,
        selectedUsers,
        filteredUsers,
        statusClassMap,
        formatDate,
        setActiveTab,
        setSearchTerm,
        handleGeneralInputChange,
        handleSaveGeneral,
        handleAddUser,
        handleRemoveUser,
        handleSaveUsers,
        handleDisableGroup,
    };
};
